"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import EventCard from "@/app/components/EventCard";
import { useT } from "@/hooks/useT";

interface MarketPulseProps {
  initialMarkets?: any[];
  /** `teamName.toLowerCase()` → logo URL. Without it, sports cards
   *  render team rows with no crest because Polymarket sets every
   *  nested `market.image` to the generic event sport icon. */
  teamLogos?: Record<string, string>;
}

/**
 * Market Pulse — hot prediction markets right now.
 *
 * Replaces the old TrendingMarkets + LiveFeed sections. The activity
 * stream already lives in the hero (HeroV3Tape), so this block focuses
 * purely on surfacing 6 real markets that people can click into.
 *
 * Server-rendered from the SSR payload in page.tsx, re-validated by
 * Next's ISR (60s) so first paint is instant and the data stays fresh.
 */
export default function MarketPulse({ initialMarkets = [], teamLogos }: MarketPulseProps) {
  const t = useT();
  const markets = initialMarkets.slice(0, 6);

  return (
    <section className="py-20 lg:py-28 bg-white dark:bg-[#0a0a0b]">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10 lg:mb-14">
          <div className="max-w-2xl">
            <div className="font-inter-tight text-[11px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
              {t("home.pulse.eyebrow")}
            </div>
            <h2 className="font-inter-tight font-semibold text-gray-900 dark:text-white tracking-[-0.02em] text-[34px] lg:text-[48px] leading-[1.05]">
              {t("home.pulse.title")}
            </h2>
            <p className="mt-3 text-gray-600 dark:text-gray-400 font-inter-tight text-base lg:text-lg">
              {t("home.pulse.body")}
            </p>
          </div>
          <Link
            href="/markets"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[5px] border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 font-inter-tight font-medium text-sm hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors self-start lg:self-auto"
          >
            {t("home.pulse.cta")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Markets grid */}
        {markets.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {markets.map((m, i) => (
              <EventCard
                key={m.id || m.slug || i}
                id={m.id}
                slug={m.slug}
                title={m.title}
                image={m.image ?? null}
                icon={m.icon ?? null}
                endDate={m.endDate ?? null}
                closed={m.closed}
                active={m.active}
                volume={Number(m.volume) || 0}
                volume24hr={Number(m.volume24hr) || 0}
                liquidity={Number(m.liquidity) || 0}
                markets={m.markets || []}
                source={(m.source as any) || "polymarket"}
                tags={m.tags}
                eventTeams={m.teams}
                teamLogos={teamLogos}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-[220px] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-white/[0.02]"
                aria-hidden
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
